/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */
// This file contains the Finsemble config service
//   1) this service processes / builds the overall configuration object
//   2) it also responds to queries for config from other services and components.

import { BaseService } from "../baseService";
import { DistributedStoreClient } from "../../clients/distributedStoreClient";
import { ConfigUtilInstance as ConfigUtil } from "../../common/configUtil";
import { DeprecatedConfigUtilInstance as DeprecatedConfigUtil } from "./migratingDeprecatedConfigUtil";
import { SchemaUtilInstance as SchemaUtil } from "./schemaUtil";
import Logger from "../../clients/logger";
import * as Util from "../../common/util";
import { toast } from "../../common/toast";
import { series as asyncSeries, each as asyncEach } from "async";
import SystemManagerClient from "../../common/systemManagerClient";
import SmartDesktopClientHandler from "./smartDesktopClientHandler";
import InteropAdmin from "../../clients/Interop/InteropAdmin";
import { AppDefinition } from "../Interop/types";
import StoreModel from "../../clients/StoreModel";
import { ResponderMessage, RouterClient } from "../../clients/routerClient";
import { StandardCallback, StandardError, StandardPromise } from "../../types";
import "./preferencesService";
import debounce from "lodash/debounce";

const SUBSCHEMA_IDENTIFIER = "#/definitions/ManifestSubschema";

Logger.start();

Logger.system.log("Starting Config Service");

/**
 * Placeholder types until we have definitive definitions (elsewhere).
 */
type Manifest = Record<string, any>;
type FinsembleConfig = Record<string, any>;

type DynamicConfigRequest = {
	manifest: Manifest;
	allowOverwrites: boolean;
	finsembleConfigObject: FinsembleConfig;
	// Defaults to false (append apps)
	replaceApps?: boolean;
	// Defaults to false (append components)
	replaceComponents?: boolean;
	// Defaults to false (append services)
	replaceServices?: boolean;
	updateStore: boolean;
};

type DynamicConfigRequestRecursive = DynamicConfigRequest & {
	recursionLevel: number;
};

/**
 * Converts an appd entry into a legacy component entry.
 * @param {AppDEntry} appdEntry
 * @returns
 */
export const appdToComponent = (appdEntry: AppDefinition) => {
	return {
		...(appdEntry?.manifest ?? {}),
		isAnApp: true,
		appDConfig: appdEntry,
		component: {
			...appdEntry?.manifest?.component,
		},
	};
};

// removes all but system components from config (support config.set with replaceComponents)
const filterNonSystemComponents = (configObject) => {
	let changed = false;
	for (const key in configObject) {
		// any component without explicit config where component.category === "system" is considered a non-system component and
		// deleted.  Therefore, if component or component.category properties are not defined, must be a non-system component
		if (
			!configObject[key].component ||
			!configObject[key].component.category ||
			configObject[key].component.category !== "system"
		) {
			Logger.system.debug("filterNonSystemComponents deleting", key);
			delete configObject[key];
			changed = true;
		}
	}
	Logger.system.debug("filterNonSystemComponents after", configObject, changed);
	return changed;
};

// removes all but system services from config (support config.set with replaceServices)
const filterNonSystemServices = (configObject: object) => {
	let changed = false;
	for (const key in configObject) {
		if (!configObject[key].category || configObject[key].category !== "system") {
			Logger.system.debug("filterNonSystemServices deleting", key);
			delete configObject[key];
			changed = true;
		}
	}
	Logger.system.debug("filterNonSystemServices after", configObject, changed);
	return changed;
};

/**
 * When new config is loaded, either due to the config service initializing or from a processAndSet action, that
 * config is validated against the system's JSON schema. This is done as a background task and will output as warnings
 * in the central logger. Since this operation is expensive, we use debounce to ensure that it only occurs at most
 * once per 6 seconds. This deals with situations where a client might send a flurry of processAndSet actions. That flurry
 * will result in only 1 or 2 validation passes.
 */
const debouncedValidateConfig = debounce(SchemaUtil.validateConfig.bind(SchemaUtil), 6000, {
	trailing: false,
	leading: true,
	maxWait: 12000,
});

/**
 * ConfigService. This service process and builds the overall configuration object.
 * It also responds to queries for config from other services and components.
 */
class ConfigService extends BaseService {
	/**
	 * This flag flips to true when the service is ready. Any client calls that are received (through the router listeners)
	 * prior to the service being ready will result in errors in the central logger. This should never happen since Finsemble's dependencies
	 * are structured to prevent this.
	 */
	serviceReady: boolean = false;

	configStore: StoreModel | null = null;

	// holds the complete processed manifest.  The store holds the master version of the data.  This copy is used for the initial input, preprocessing, and schema verification of the data.
	manifest: Manifest = {};

	interopAdmin = new InteropAdmin();

	debouncedRecheckForSchemaErrors: Function;

	/**
	 * @param {string} params.name name of the service
	 * @param {object} params.startupDependencies
	 * @param {array} params.startupDependencies.clients Clients to wait on before initializing the service
	 * @param {array} params.startupDependencies.services Services to wait on before initializing the service
	 * @param {object} params.shutdownDependencies
	 * @param {array} params.shutdownDependencies.services Services to wait on before shutting down the service
	 * */
	constructor(params) {
		super(params);
		this.listeners = {};
		this.onBaseServiceReady((done: () => void) => {
			this.doInit(done);
		});
		this.start();
		this.debouncedRecheckForSchemaErrors = debounce(this.recheckForSchemaErrors.bind(this), 500, {
			trailing: true,
			leading: false,
			maxWait: 2500,
		});
	}

	/**
	 * Creates the public interface with the rest of the application.
	 * Each of the handlers below simply receive a request, pass it off to a member function that does the work, and then sends the result back to the function that sent the original message (if necessary).
	 * @memberof ConfigService
	 */
	createRouterEndpoints() {
		RouterClient.addResponder("configService.processAndSet", (err, message) => {
			this.serviceReady || Logger.system.error("configService invoked before ready", message);
			if (err || !message) {
				return Logger.system.error(err);
			}
			return this.processAndSet(message);
		});

		// @deprecated remove in 7.0
		RouterClient.addPubSubResponder("Config.changeNotification", {
			newConfig: null,
			finsemble: this.manifest.finsemble,
		});

		// New Handlers for Local Store
		RouterClient.addPubSubResponder(/configService*/, {});

		RouterClient.addResponder("configService.getValue", (err, message) => {
			this.serviceReady || Logger.system.error("configService invoked before ready", message);
			if (err || !message) {
				return Logger.system.error(err);
			}
			return this.getValue(message.data, (getValueErr, data) => {
				message.sendQueryResponse(getValueErr, data);
			});
		});

		RouterClient.addResponder("configService.getValues", (err, message) => {
			this.serviceReady || Logger.system.error("configService invoked before ready", message);
			if (err || !message) {
				return Logger.system.error(err);
			}
			return this.getValues(message.data.fields, (getValuesErr, data) => {
				message.sendQueryResponse(getValuesErr, data);
			});
		});

		RouterClient.addResponder("configService.setValue", (err, message) => {
			this.serviceReady || Logger.system.error("configService invoked before ready", message);

			if (err || !message) {
				return Logger.system.error(err);
			}
			const trustedRequest = RouterClient.trustedMessage(message);
			if (!trustedRequest) {
				return message.sendQueryResponse("setValue failed. Only trusted (root domain) sources can set config.");
			}

			this.setValue(message.data, (setValueErr) => {
				message.sendQueryResponse(setValueErr, message.data);
			});
		});

		RouterClient.addResponder("configService.setValues", (err, message) => {
			this.serviceReady || Logger.system.error("configService invoked before ready", message);

			if (err || !message) {
				return Logger.system.error(err);
			}
			const trustedRequest = RouterClient.trustedMessage(message);
			if (!trustedRequest) {
				return message.sendQueryResponse("setValue failed. Only trusted (root domain) sources can set config.");
			}

			this.setValues(message.data, (setValuesErr) => {
				message.sendQueryResponse(setValuesErr, message.data);
			});
		});

		RouterClient.addResponder("configService.addListener", (err, message) => {
			this.serviceReady || Logger.system.error("configService invoked before ready", message);

			if (err || !message) {
				return Logger.system.error(err);
			}

			this.addListener(message.data);
			message.sendQueryResponse(err, message.data);
		});
	}

	async recheckForSchemaErrors(finsembleConfig) {
		// The master version of the config data is in the store. This local copy is only for verification to log any potential schema errors.
		this.manifest.finsemble = finsembleConfig;
		Logger.system.log("recheckForSchemaErrors");
		await DeprecatedConfigUtil.modifyConfigBasedOnDeprecatedDefinitions("dynamicConfig", this.manifest);
		await SchemaUtil.walkSchemaAndSetConfigDefaults(this.manifest);
		await debouncedValidateConfig(this.manifest, SUBSCHEMA_IDENTIFIER);
	}

	/**
	 * Updates AppD config in place, adapting properties as needed to latest definitions.
	 *
	 * @param {object} appDConfig application config in appD format
	 *
	 * @memberof MyService
	 */
	updateAppDDefinition(appDConfig: AppDefinition): AppDefinition {
		// if appService flag is set, then automatically set all the associated service properties.
		if (appDConfig?.manifest?.appService) {
			appDConfig.manifest.bootParams = appDConfig.manifest.bootParams ?? {};
			appDConfig.manifest.bootParams.autoStart = appDConfig.manifest.bootParams.autoStart ?? true;
			appDConfig.manifest.bootParams.stage = appDConfig.manifest.bootParams.stage ?? "preuser";

			appDConfig.manifest.window = appDConfig.manifest.window ?? {};
			appDConfig.manifest.window.options = appDConfig.manifest.window.options ?? {};
			appDConfig.manifest.window.options.autoShow = appDConfig.manifest.window.options.autoShow ?? false;
			appDConfig.manifest.window.frame = appDConfig.manifest.window.frame ?? true;

			appDConfig.manifest.foreign = appDConfig.manifest.foreign ?? {};
			appDConfig.manifest.foreign.components = appDConfig.manifest.foreign.components ?? {};
			appDConfig.manifest.foreign.components["Window Manager"] =
				appDConfig.manifest.foreign.components["Window Manager"] ?? {};
			appDConfig.manifest.foreign.components["Window Manager"].FSBLHeader = false;

			appDConfig.manifest.foreign.components["windowService"] =
				appDConfig.manifest.foreign.components["windowService"] ?? {};
			appDConfig.manifest.foreign.components["windowService"].manageWindowMovement = false;

			appDConfig.manifest.foreign = appDConfig.manifest.foreign ?? {};
			appDConfig.manifest.foreign.components = appDConfig.manifest.foreign.components ?? {};
			appDConfig.manifest.foreign.components["App Launcher"] =
				appDConfig.manifest.foreign.components["App Launcher"] ?? {};
			appDConfig.manifest.foreign.components["App Launcher"].launchableByUser = false;

			appDConfig.manifest.component = appDConfig.manifest.component ?? {};
			appDConfig.manifest.component.singleton = appDConfig.manifest.component.singleton ?? true;
			// Since an app service is a component from Finsemble's perspective, spawnOnStartup has to be set when autoStart is set
			appDConfig.manifest.component.spawnOnStartup = appDConfig.manifest.bootParams.autoStart;

			Logger.system.debug("updateAppDDefinition appService", appDConfig);
		}
		return appDConfig;
	}

	/**
	 * Process any appD definitions, updating them in place as needed and copying to finsemble.components.
	 *
	 * @param {*} configRoot the top-level config object that contains finsemble config
	 *
	 * @memberof MyService
	 */
	processAppDConfig(configRoot: any) {
		if (typeof configRoot.finsemble.appd === "object") {
			configRoot.finsemble.components = configRoot.finsemble.components ?? {};
			const { appd }: { appd: Record<string, AppDefinition> } = configRoot.finsemble;
			for (const entry of Object.entries(appd)) {
				let [appName, app] = entry;
				Logger.system.log(`Application "${app.appId}" added or updated in components`, appName, app);

				app = this.updateAppDDefinition(app);

				if (configRoot.finsemble.components[app.appId]) {
					Logger.system.warn(
						`processAppDConfig: addD config is overwriting a preexisting component with the same name as the app - ${app.appId}`
					);
				}

				configRoot.finsemble.components[app.appId] = appdToComponent(app);
			}
		} else {
			Logger.system.error("Expected manifest.finsemble.appd to be an object, but continuing without app config.");
		}
	}

	/**
	 * Handles the startup-requirements of the service.
	 * @memberof MyService
	 */
	doInit(callback: () => void) {
		const initializeSmartDesktopClient = async () => {
			await SmartDesktopClientHandler.initialize();
		};

		const getManifest = async () =>
			new Promise<void>(async (resolve) => {
				const { err, data: initialManifest } = await ConfigUtil.getInitialManifest();
				Logger.system.log("forceObjectsToLogger", "getManifest: ConfigUtil.getInitialManifest", err, initialManifest);

				if (!err && initialManifest) {
					const { err: err2, data: completeManifest } = await this.processManifest(initialManifest);
					if (completeManifest) this.manifest = completeManifest;
					if (err2) Logger.system.error(`configService:getManifest() Error getting initial manifest ${err2}`);
					Logger.system.log("forceObjectsToLogger", "getManifest: processManifest", err2, completeManifest);
					resolve();
				} else {
					Logger.system.error(`configService:getManifest() Error getting initial manifest ${err}`);
					resolve();
				}
			});

		const processInitialAppDConfig = (done: () => void) => {
			// Process any appD config now.  After InteropService has started, the boot task AddAppDefinitionsToDesktopAgentTask will send these app definitions to the InteropService.
			this.processAppDConfig(this.manifest);
			done();
		};

		const handleDeprecatedConfig = async () => {
			try {
				await DeprecatedConfigUtil.modifyConfigBasedOnDeprecatedDefinitions("mainConfig", this.manifest);
				Logger.system.log("modifyConfigBasedOnDeprecatedDefinitions done");
			} catch (err) {
				Logger.system.error("modifyConfigBasedOnDeprecatedDefinitions failed.  Error caught", err);
			}
		};

		const walkSchemaForDefaults = async () => {
			try {
				await SchemaUtil.walkSchemaAndSetConfigDefaults(this.manifest);
			} catch (err) {
				Logger.system.error("walkSchemaForDefaults failed.  Error caught", err);
			}
		};

		const verifyConfig = async () => {
			Logger.system.debug("JSON Schema: initial validation");
			const err = await debouncedValidateConfig(this.manifest, SUBSCHEMA_IDENTIFIER);
			if (err) {
				Logger.system.error(`Manifest Error: ${err}`);
			}
			Logger.system.log(
				"forceObjectsToLogger",
				"APPLICATION LIFECYCLE:STARTUP: ************* FULL PROCESSED MANIFEST *************",
				this.manifest
			);
			console.log("APPLICATION LIFECYCLE:STARTUP: ************* FULL PROCESSED MANIFEST *************", this.manifest);
		};

		const createStore = (done: () => void) => {
			this.initializeDataStore(this.manifest, () => {
				Logger.system.log("initializeDataStore complete");
				this.createRouterEndpoints();
				done();
			});
		};

		const setUpStoreListeners = (done: () => void) => {
			this.configStore?.addListener({ field: "finsemble" }, async (err, data) => {
				Logger.system.debug("forceObjectsToLogger", "JSON Schema: store changed so recheckForSchemaErrors", data);
				// Must await on the debounce call or recheckForSchemaError will be called extra time at the end due to how debounce timing works.
				await this.debouncedRecheckForSchemaErrors(data.value);
			});
			done();
		};

		asyncSeries(
			[
				initializeSmartDesktopClient,
				getManifest,
				processInitialAppDConfig,
				handleDeprecatedConfig,
				walkSchemaForDefaults,
				verifyConfig,
				createStore,
				setUpStoreListeners,
			],
			() => {
				this.serviceReady = true;
				callback();
			}
		);
	}

	/**
	 * Fetches the config files from the provided importURLs array and returns
	 * them as an array of objects.
	 *
	 * Ordering is preserved, which is important because entries in later files should override entries in earlier files.
	 */
	private async fetchConfigImports(importURLs: string[]) {
		Logger.system.debug("forceObjectsToLogger", "importURLs", importURLs);

		const promises = importURLs.map((url) => ConfigUtil.fetchConfigFile(url));

		const fetchResults = await Promise.all(promises);

		return fetchResults.flatMap((result) => (result?.data ? [result.data] : []));
	}

	private processImportedConfig(dynamicConfigRequest: DynamicConfigRequestRecursive) {
		const {
			manifest,
			allowOverwrites,
			recursionLevel,
			finsembleConfigObject,
			replaceApps = false,
			replaceComponents = false,
			replaceServices = false,
			updateStore,
		} = dynamicConfigRequest;

		const commentRegex = /comment.*/; // "comment" properties

		// if updateStore is true then update store field with new value
		const conditionalStoreValue = (shouldUpdateStore: boolean, field: string, value: any) => {
			if (shouldUpdateStore) {
				this.configStore?.setValue({ field, value });
			}
		};

		const appd = () => {
			if (allowOverwrites && replaceApps) {
				manifest.finsemble.appd = {};
				if (updateStore) {
					this.configStore?.setValue({
						field: "finsemble.appd",
						value: manifest.finsemble.appd,
					});
				}
			} else {
				manifest.finsemble.appd = manifest.finsemble.appd || {};
			}
			for (const newApp in finsembleConfigObject.appd) {
				Logger.system.debug("adding appd app", newApp);
				if (!allowOverwrites && manifest.finsemble.appd.hasOwnProperty(newApp)) {
					console.warn(
						"finsemble appd app config",
						newApp,
						"cannot be overwritten by third-party config",
						finsembleConfigObject.appd[newApp]
					);
				} else if (recursionLevel > 0 && manifest.finsemble.appd.hasOwnProperty(newApp)) {
					Logger.system.info(
						"finsemble appd app config",
						newApp,
						"is being overwritten by appd app config",
						finsembleConfigObject.appd[newApp]
					);
					manifest.finsemble.appd[newApp] = finsembleConfigObject.appd[newApp];
					conditionalStoreValue(updateStore, `finsemble.appd.${newApp}`, finsembleConfigObject.appd[newApp]);
				} else {
					manifest.finsemble.appd[newApp] = finsembleConfigObject.appd[newApp];
					conditionalStoreValue(updateStore, `finsemble.appd.${newApp}`, finsembleConfigObject.appd[newApp]);
				}
			}
		};

		const components = () => {
			if (allowOverwrites && replaceComponents) {
				const changed = filterNonSystemComponents(manifest.finsemble.components);
				if (changed && updateStore) {
					this.configStore?.setValue({
						field: "finsemble.components",
						value: manifest.finsemble.components,
					});
				}
			} else {
				manifest.finsemble.components = manifest.finsemble.components || {};
			}
			for (const newComponent in finsembleConfigObject.components) {
				Logger.system.debug("adding component", newComponent);
				if (!allowOverwrites && manifest.finsemble.components.hasOwnProperty(newComponent)) {
					console.warn(
						"finsemble component config",
						newComponent,
						"cannot be overwritten by third-party component config",
						finsembleConfigObject.components[newComponent]
					);
				} else if (recursionLevel > 0 && manifest.finsemble.components.hasOwnProperty(newComponent)) {
					Logger.system.info(
						"finsemble component config",
						newComponent,
						"is being overwritten by component config",
						finsembleConfigObject.components[newComponent]
					);
					manifest.finsemble.components[newComponent] = finsembleConfigObject.components[newComponent];
					conditionalStoreValue(
						updateStore,
						`finsemble.components.${newComponent}`,
						finsembleConfigObject.components[newComponent]
					);
				} else {
					manifest.finsemble.components[newComponent] = finsembleConfigObject.components[newComponent];
					conditionalStoreValue(
						updateStore,
						`finsemble.components.${newComponent}`,
						finsembleConfigObject.components[newComponent]
					);
				}
			}
		};

		const services = () => {
			if (allowOverwrites && replaceServices) {
				const changed = filterNonSystemServices(manifest.finsemble.services);
				if (changed && updateStore) {
					this.configStore?.setValue({
						field: "finsemble.services",
						value: manifest.finsemble.services,
					});
				}
			} else {
				manifest.finsemble.services = manifest.finsemble.services || {};
			}
			for (const newService in finsembleConfigObject.services) {
				Logger.system.debug("adding service", newService);
				if (!allowOverwrites && manifest.finsemble.services.hasOwnProperty(newService)) {
					console.warn(
						"finsemble service config",
						newService,
						"cannot be overwritten by third-party service config",
						finsembleConfigObject.services[newService]
					);
				} else if (recursionLevel > 0 && manifest.finsemble.services.hasOwnProperty(newService)) {
					Logger.system.info(
						"finsemble service config",
						newService,
						"is being overwritten by service config",
						finsembleConfigObject.services[newService]
					);
					manifest.finsemble.services[newService] = finsembleConfigObject.services[newService];
					conditionalStoreValue(
						updateStore,
						`finsemble.services.${newService}`,
						finsembleConfigObject.services[newService]
					);
				} else {
					manifest.finsemble.services[newService] = finsembleConfigObject.services[newService];
					conditionalStoreValue(
						updateStore,
						`finsemble.services.${newService}`,
						finsembleConfigObject.services[newService]
					);
				}
			}
		};

		const workspaceTemplates = () => {
			manifest.finsemble.workspaceTemplates = manifest.finsemble.workspaceTemplates || {};
			for (const newTemplate in finsembleConfigObject.workspaceTemplates) {
				Logger.system.debug("adding template", newTemplate);
				if (!allowOverwrites && manifest.finsemble.workspaceTemplates.hasOwnProperty(newTemplate)) {
					console.warn(
						"finsemble template config",
						newTemplate,
						"cannot be overwritten by third-party template config",
						finsembleConfigObject.workspaceTemplates[newTemplate]
					);
				} else if (recursionLevel > 0 && manifest.finsemble.workspaceTemplates.hasOwnProperty(newTemplate)) {
					Logger.system.info(
						"finsemble template config",
						newTemplate,
						"is being overwritten by template config",
						finsembleConfigObject.workspaceTemplates[newTemplate]
					);
					manifest.finsemble.workspaceTemplates[newTemplate] = finsembleConfigObject.workspaceTemplates[newTemplate];
					conditionalStoreValue(
						updateStore,
						`finsemble.workspaceTemplates.${newTemplate}`,
						finsembleConfigObject.workspaceTemplates[newTemplate]
					);
				} else {
					manifest.finsemble.workspaceTemplates[newTemplate] = finsembleConfigObject.workspaceTemplates[newTemplate];
					conditionalStoreValue(
						updateStore,
						`finsemble.workspaceTemplates.${newTemplate}`,
						finsembleConfigObject.workspaceTemplates[newTemplate]
					);
				}
			}
		};

		// the config for apps (in appD), components, and services all require essentially the same special handling because these properties my be overwritten or appended to in different contexts
		Object.keys(finsembleConfigObject).forEach((key) => {
			if (commentRegex.test(key)) {
				// delete any comment properties
				Logger.system.debug("ignoring comment", key);
				delete finsembleConfigObject[key];
			} else if (key === "appd") {
				appd();
			} else if (key === "components") {
				components();
			} else if (key === "services") {
				services();
			} else if (key === "workspaceTemplates") {
				workspaceTemplates();
			} else if (key === "importConfig") {
				// doing nothing on first pass -- process importConfig URLs separately at the end (see below)
			} else if (key === "importThirdPartyConfig") {
				// doing nothing on first pass -- process importThirdPartyConfig URLs separately at the end (see below)
			} else {
				Logger.system.debug("adding key", key);
				if (!allowOverwrites && manifest.finsemble.hasOwnProperty(key)) {
					console.warn("finsemble config property", key, "cannot be overwritten by third-party config");
				} else if (recursionLevel > 0 && manifest.finsemble.hasOwnProperty(key)) {
					Logger.system.info("finsemble config property", key, "is being overwritten");
					manifest.finsemble[key] = finsembleConfigObject[key];
					conditionalStoreValue(updateStore, `finsemble.${key}`, finsembleConfigObject[key]);
				} else {
					manifest.finsemble[key] = finsembleConfigObject[key];
					conditionalStoreValue(updateStore, `finsemble.${key}`, finsembleConfigObject[key]);
				}
			}
		});
	}

	/**
	 * Process a set of finsemble config properties, adding them to manifest.finsemble. The config may contain
	 * other config URLs to fetch and import;
	 * This function uses the recursive function processFinsembleConfigRecursive (traveling depth-first through imported URLs).
	 * @param {object} params
	 * @param {object} params.manifest  manifest object containing finsemble property that will be updated with new config
	 * @param {boolean} params.allowOverwrites if true then new config is allowed to overwrite old, otherwise override cause error
	 * @param {object} params.finsembleConfigObject the finsemble config to process (i.e. add to main.finsemble)
	 * @param {boolean} params.replaceApps if true then replace preexisting appD definition (which contains apps)
	 * @param {boolean} params.replaceComponents if true then replace preexisting component definitions
	 * @param {boolean} params.replaceServices if true then replace preexisting service definitions
	 * @param {boolean} params.updateStore if true then update the store as config is set (this happens when newConfig is defined through config.set())
	 * @param {function} callback
	 * @private
	 */
	async processFinsembleConfig(
		dynamicConfigRequest: DynamicConfigRequest,
		cb?: (manifest: Manifest) => void
	): Promise<Manifest> {
		// This first pass ensures that the imports are fetched, making use of the manifest macros that are available in the
		// initial manifest
		let manifest = await this.processFinsembleConfigRecursive({
			...dynamicConfigRequest,
			recursionLevel: 0,
		});

		// Take another pass to make sure the (now complete set of) manifest macros are applied throughout the
		// assembled config
		manifest = await this.processFinsembleConfigRecursive({ ...dynamicConfigRequest, recursionLevel: 0 });
		cb?.(manifest);
		return manifest;
	}

	/**
	 * Does the work for processFinsembleConfig. This function uses an additional recursionLevel parameter
	 * in order to determine whether the recursion has gone too far.
	 */
	private async processFinsembleConfigRecursive(
		dynamicConfigRequest: DynamicConfigRequestRecursive
	): Promise<Manifest> {
		const { manifest, recursionLevel, finsembleConfigObject } = dynamicConfigRequest;

		// if recursion has descended this deep then probably circular imports, so stop importing
		if (recursionLevel > 50) {
			// note this is the recursive depth of imports, not the import count (so should never happen unless import loop)
			const errorMsg = "Configuration Error: importConfig may have a loop (recursion depth exceeded).";
			SystemManagerClient.systemLog({ error: true }, errorMsg);
			Logger.system.error(errorMsg);
			toast("dev", "ONCE-SINCE-STARTUP", "FSBL-Internal-Config-Error", errorMsg);
			return manifest;
		}

		Logger.system.debug("forceObjectsToLogger", "*** entering processFinsembleConfig ***", finsembleConfigObject);

		// Fetch the imports for the current file and then process them one at a time by passing
		// each imported config recursively into this same function. It's very important to wait
		// for each file to process before moving to the next because latter files should override
		// earlier ones (consider the entire tree). This is why we use for loops instead of Promise.all()
		const mergeImportsIntoManifest = async () => {
			const importConfigs = await this.fetchConfigImports(finsembleConfigObject.importConfig ?? []);
			const importThirdPartyConfigs = await this.fetchConfigImports(finsembleConfigObject.importThirdPartyConfig ?? []);
			for (const i in importConfigs) {
				await this.processFinsembleConfigRecursive({
					...dynamicConfigRequest,
					recursionLevel: recursionLevel + 1,
					finsembleConfigObject: importConfigs[i],
					allowOverwrites: true,
				});
			}
			// Third party configs don't allow overwrites
			for (const i in importThirdPartyConfigs) {
				await this.processFinsembleConfigRecursive({
					...dynamicConfigRequest,
					recursionLevel: recursionLevel + 1,
					finsembleConfigObject: importThirdPartyConfigs[i],
					allowOverwrites: false,
				});
			}
		};

		// Resolve the manifest macros that are known up to now
		ConfigUtil.resolveManifestMacros(manifest, finsembleConfigObject);
		// Load any imports
		await mergeImportsIntoManifest();
		// Now after importing we process manifest macros again because more may have been defined inside the imports
		ConfigUtil.resolveManifestMacros(manifest, finsembleConfigObject);
		// Finally, process the imported config
		this.processImportedConfig(dynamicConfigRequest);

		return manifest;
	}

	/**
	 * the main routine kicking config processing
	 *
	 * @param {object} manifest
	 * @param {function} processManifestCallback
	 * @private
	 */
	async processManifest(manifest: Manifest): StandardPromise<Manifest> {
		if (typeof manifest.finsemble !== "undefined") {
			// assemble/update the finsemble config object (first time this is call the finsemble object updates itself, but simpler code)
			const PFCParams = {
				manifest,
				allowOverwrites: true,
				finsembleConfigObject: manifest.finsemble,
				replaceApps: false,
				replaceComponents: false,
				replaceServices: false,
				updateStore: false,
			};
			const processedManifest = await this.processFinsembleConfig(PFCParams);
			return { data: processedManifest };
		} else {
			const errorMsg = "'finsemble' property not found in manifest";
			SystemManagerClient.systemLog({ error: true }, errorMsg);
			Logger.system.error(errorMsg);
			return { err: errorMsg, data: manifest };
		}
	}

	/**
	 *
	 *
	 * @param {object} manifest
	 * @param {function} callback
	 */
	initializeDataStore(manifest: Manifest, callback: () => void) {
		Logger.system.debug("initializeDataStore");
		DistributedStoreClient.createStore(
			{
				store: "Finsemble-Configuration-Store",
				global: false,
				values: manifest,
			},
			(err, newStore) => {
				if (err || !newStore) {
					Logger.system.error("CreateStore Error", err);
				} else {
					this.configStore = newStore;
					Logger.console.log("initializeDataStore complete");
					callback();
				}
			}
		);
	}

	get(queryEvent: ResponderMessage<any>) {
		const { field } = queryEvent.data && queryEvent.data.hasOwnProperty("field");

		const getValuesCallback = (err: StandardError, storeData?: Record<string, any>) => {
			if (storeData) {
				Logger.system.log("get responseData", storeData);
				queryEvent.sendQueryResponse(null, storeData);
			} else {
				const error = `field ${field} error: ${err}`;
				Logger.system.warn(error);
				queryEvent.sendQueryResponse(error, null);
			}
		};

		// data to be retrieved stored in configReference
		if (field) {
			this.configStore?.getValues([{ field }], getValuesCallback); // this case returns a sub-field in the store
		} else {
			this.configStore?.getAll(getValuesCallback); // this case returns everything in the store
		}
	}

	set(queryEvent: ResponderMessage<any>) {
		const getValuesCallback = (err: StandardError, storeData?: Record<string, any>) => {
			if (storeData) {
				Logger.system.log("get responseData", storeData);
				queryEvent.sendQueryResponse(null, storeData);
			} else {
				const error = `field ${storeData} error: ${err}`;
				Logger.system.warn(error);
				queryEvent.sendQueryResponse(error, null);
			}
		};

		if (queryEvent.data?.hasOwnProperty("field")) {
			if (!queryEvent.data.field.startsWith("finsemble.")) {
				queryEvent.sendQueryResponse("You cannot modify this part of the config", null);
			} else {
				const { field, value }: { field: string; value: any } = queryEvent.data;
				this.configStore?.setValues([
					{
						field,
						value,
					},
				]);
				this.configStore?.getValues([{ field: "field" }], getValuesCallback);
			}
		} else {
			queryEvent.sendQueryResponse("Invalid Request", null);
		}
	}

	processAndSet(queryEvent: ResponderMessage<any>) {
		const trustedRequest = RouterClient.trustedMessage(queryEvent);
		let { newConfig } = queryEvent.data;
		const overwrite = !!queryEvent.data.overwrite;
		const replace = !!queryEvent.data.replace;
		Logger.system.debug("forceObjectsToLogger", "Config.processAndSet", trustedRequest, newConfig);

		if (overwrite && !trustedRequest) {
			queryEvent.sendQueryResponse(
				"Config.processAndSet Rejected: only same domain components using default SharedWorker transport can overwrite config",
				null
			);
			Logger.error(
				`processAndSet request blocked from an untrusted app "${queryEvent.header.origin}". Only apps on the SharedWorker transport can call processAndSet()`
			);
		} else {
			/**
			 * If `finsemble.appd` is being set then we're going to *also* set
			 * `finsemble.components` because much legacy code depends on it (launcher.ts, dynamic config toolbar menus).
			 * The underlying distributed store will pick up on these changes and alert config listeners.
			 */

			// Update AppD in place before these app definitions are sent to Interop Service
			if (newConfig?.appd) {
				this.processAppDConfig({
					finsemble: newConfig,
				});
			}
			const PFCParams = {
				manifest: this.manifest,
				allowOverwrites: overwrite,
				finsembleConfigObject: newConfig,
				replaceApps: replace,
				replaceComponents: replace,
				replaceServices: replace,
				updateStore: true,
			};
			this.processFinsembleConfig(PFCParams, async (manifest) => {
				Logger.system.log("forceObjectsToLogger", "Config.processAndSet revised manifest", manifest); // this log referenced in FAQ so don't change
				// only by setting one last value and waiting for callback do we know all the previous setValues are done too
				this.configStore?.setValue({ field: "finsemble.configSetDone", value: true }, () => {
					queryEvent.sendQueryResponse(null, manifest.finsemble);
				});

				if (newConfig?.appd) {
					await this.interopAdmin.addAppDefinitions(this.manifest.finsemble.appd);
				}
				RouterClient.publish("Config.changeNotification", {
					newConfig,
					finsemble: manifest.finsemble,
				});
			});
		}
	}

	getValue(data: string | { field: string }, cb: StandardCallback) {
		this.configStore?.getValue(data, cb);
	}

	getValues(fields: { field: string }[], cb: StandardCallback) {
		if (!fields) {
			return this.configStore?.getAll(cb);
		}
		this.configStore?.getValues(fields, cb);
	}

	setValue(params: { field: string; value: any }, cb: StandardCallback) {
		this.configStore?.setValue(params, cb);
	}

	setValues(fields: { field: string; value: any }[], cb: StandardCallback) {
		asyncEach(
			fields,
			(field, done) => {
				this.setValue(field, done);
			},
			(err) => {
				cb(err);
			}
		);
	}

	addListener(fieldChain: string) {
		const storeChangeHandler = (err: StandardError, change?: { field: string }) => {
			if (change) {
				change.field = change.field.replace("Finsemble-Configuration-Store", "configService");
				RouterClient.publish(change.field, change);
			} else {
				Logger.system.warn("configService didn't receive a valid change from DistributedStoreClient", err);
			}
		};

		if (!this.listeners[fieldChain]) {
			this.listeners[fieldChain] = true;
			const request = fieldChain === "configService" ? {} : { field: fieldChain.replace("configService.", "") };
			this.configStore?.addListener(request, storeChangeHandler);
		}
	}
}

export default new ConfigService({ name: "configService" });
